import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  FaCheck,
  FaTimes,
  FaStar,
  FaArrowRight,
  FaRocket,
  FaCrown,
  FaGem,
  FaUsers,
  FaUtensils,
  FaChartLine,
  FaShieldAlt,
  FaMobileAlt,
  FaClock,
  FaHeadset,
  FaDatabase,
  FaCloud,
  FaLock,
  FaGlobe,
  FaCog,
  FaQuestionCircle,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';

const Pricing = () => {
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const pricingPlans = [
    {
      name: "Starter",
      description: "Perfect for small catering businesses just getting started",
      price: { monthly: 999, annual: 9990 },
      originalPrice: { monthly: 1499, annual: 14990 },
      icon: <FaRocket className="w-8 h-8" />,
      gradient: "from-primary to-primary/80",
      popular: false,
      features: [
        { name: "Up to 50 orders per month", included: true },
        { name: "Basic menu management", included: true },
        { name: "Order tracking", included: true },
        { name: "Customer database (100 contacts)", included: true },
        { name: "Basic reports", included: true },
        { name: "Email support", included: true },
        { name: "Mobile app access", included: true },
        { name: "Inventory management", included: false },
        { name: "Advanced analytics", included: false },
        { name: "Multi-location support", included: false },
        { name: "API access", included: false },
        { name: "Priority support", included: false }
      ]
    },
    {
      name: "Professional",
      description: "Ideal for growing catering businesses with more complex needs",
      price: { monthly: 1999, annual: 19990 },
      originalPrice: { monthly: 2999, annual: 29990 },
      icon: <FaCrown className="w-8 h-8" />,
      gradient: "from-primary/90 to-secondary",
      popular: true,
      features: [
        { name: "Up to 200 orders per month", included: true },
        { name: "Advanced menu management", included: true },
        { name: "Real-time order tracking", included: true },
        { name: "Customer database (500 contacts)", included: true },
        { name: "Advanced reports & analytics", included: true },
        { name: "Priority email support", included: true },
        { name: "Mobile app access", included: true },
        { name: "Inventory management", included: true },
        { name: "Staff management", included: true },
        { name: "Multi-location support", included: false },
        { name: "API access", included: false },
        { name: "Phone support", included: false }
      ]
    },
    {
      name: "Enterprise",
      description: "For large catering operations with advanced requirements",
      price: { monthly: 3999, annual: 39990 },
      originalPrice: { monthly: 5999, annual: 59990 },
      icon: <FaGem className="w-8 h-8" />,
      gradient: "from-secondary to-primary",
      popular: false,
      features: [
        { name: "Unlimited orders", included: true },
        { name: "Full menu management suite", included: true },
        { name: "Real-time order tracking", included: true },
        { name: "Unlimited customer database", included: true },
        { name: "Advanced reports & analytics", included: true },
        { name: "24/7 phone & email support", included: true },
        { name: "Mobile app access", included: true },
        { name: "Full inventory management", included: true },
        { name: "Advanced staff management", included: true },
        { name: "Multi-location support", included: true },
        { name: "API access", included: true },
        { name: "Custom integrations", included: true }
      ]
    }
  ];

  const addOns = [
    {
      name: "Additional Staff Users",
      description: "Add more team members to your account",
      price: { monthly: 299, annual: 2990 },
      icon: <FaUsers className="w-6 h-6" />
    },
    {
      name: "Advanced Analytics",
      description: "Get deeper insights with custom reports",
      price: { monthly: 499, annual: 4990 },
      icon: <FaChartLine className="w-6 h-6" />
    },
    {
      name: "Priority Support",
      description: "Get faster response times and dedicated support",
      price: { monthly: 799, annual: 7990 },
      icon: <FaHeadset className="w-6 h-6" />
    },
    {
      name: "Custom Integrations",
      description: "Connect with your existing tools and systems",
      price: { monthly: 1299, annual: 12990 },
      icon: <FaCog className="w-6 h-6" />
    }
  ];

  const faqs = [
    {
      question: "Can I change my plan anytime?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing differences."
    },
    {
      question: "What happens if I exceed my order limit?",
      answer: "We'll notify you when you're approaching your limit. You can upgrade your plan or purchase additional orders as needed."
    },
    {
      question: "Is there a setup fee?",
      answer: "No setup fees! All plans include free setup and onboarding to help you get started quickly."
    },
    {
      question: "Do you offer custom pricing for large enterprises?",
      answer: "Yes, we offer custom pricing and features for large enterprises. Contact our sales team to discuss your specific needs."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, PayPal, and bank transfers for annual subscriptions."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-primary/5 to-secondary/5 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full text-sm font-medium text-primary border border-primary/20 mb-6">
            <FaStar className="w-4 h-4 mr-2" />
            Simple, Transparent Pricing
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Choose Your Perfect
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Catering Plan
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start with our free trial, then choose the plan that fits your business needs. 
            All plans include our core features with no hidden fees.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={`text-lg font-medium ${!isAnnual ? 'text-primary' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 ${
                isAnnual ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform duration-300 ${
                  isAnnual ? 'translate-x-9' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg font-medium ${isAnnual ? 'text-primary' : 'text-gray-500'}`}>
              Annual
            </span>
            {isAnnual && (
              <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                Save 17%
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-3xl shadow-lg border-2 transition-all duration-300 transform hover:-translate-y-2 ${
                  plan.popular 
                    ? 'border-primary shadow-2xl scale-105' 
                    : 'border-gray-200 hover:border-primary/50 hover:shadow-2xl'
                }`}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary to-secondary text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}
                
                <div className="p-8">
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${plan.gradient} rounded-2xl mb-4 mx-auto`}>
                      <div className="text-white">{plan.icon}</div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    
                    {/* Pricing */}
                    <div className="mb-6">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <span className="text-4xl font-bold text-gray-900">
                          ₹{isAnnual ? plan.price.annual : plan.price.monthly}
                        </span>
                        <span className="text-gray-500">
                          /{isAnnual ? 'year' : 'month'}
                        </span>
                      </div>
                      {plan.originalPrice && (
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-lg text-gray-400 line-through">
                            ₹{isAnnual ? plan.originalPrice.annual : plan.originalPrice.monthly}
                          </span>
                          <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-sm font-medium">
                            Save ₹{isAnnual ? plan.originalPrice.annual - plan.price.annual : plan.originalPrice.monthly - plan.price.monthly}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                          feature.included 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {feature.included ? (
                            <FaCheck className="w-3 h-3" />
                          ) : (
                            <FaTimes className="w-3 h-3" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          feature.included ? 'text-gray-700' : 'text-gray-400'
                        }`}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/register"
                    className={`block w-full text-center py-4 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-700 hover:bg-primary hover:text-white'
                    }`}
                  >
                    {plan.name === 'Starter' ? 'Start Free Trial' : 'Get Started'}
                    <FaArrowRight className="inline-block ml-2" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Add-ons & Extensions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your plan with additional features and services tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addOns.map((addon, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl mb-4">
                    <div className="text-primary">{addon.icon}</div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{addon.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{addon.description}</p>
                  <div className="text-2xl font-bold text-primary mb-4">
                    ₹{isAnnual ? addon.price.annual : addon.price.monthly}
                    <span className="text-sm text-gray-500 font-normal">
                      /{isAnnual ? 'year' : 'month'}
                    </span>
                  </div>
                  <button className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-primary hover:text-white transition-colors duration-300">
                    Add to Plan
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Got questions? We've got answers. Can't find what you're looking for? 
              <a href="#contact" className="text-primary hover:underline ml-1">Contact us</a>.
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <FaQuestionCircle className="w-5 h-5 text-primary mr-3" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of catering businesses already using our platform. 
            Start your free trial today - no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="group bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-xl hover:shadow-2xl"
            >
              Start Free Trial
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link 
              to="/contact"
              className="group border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center justify-center transform hover:scale-105 backdrop-blur-sm"
            >
              <FaPhone className="mr-2" />
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-secondary to-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3">
                  <FaUtensils className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Caterers Pro</span>
              </div>
              <p className="text-white/80 mb-6 max-w-md leading-relaxed">
                Professional catering management system designed to streamline your business operations 
                and help you deliver exceptional service to your customers.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <FaPhone className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <FaEnvelope className="w-5 h-5" />
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-6">Support</h3>
              <ul className="space-y-3 text-white/80">
                <li><a href="#" className="hover:text-white transition-colors duration-300">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">Contact Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">System Status</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">API Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-6">Company</h3>
              <ul className="space-y-3 text-white/80">
                <li><a href="#" className="hover:text-white transition-colors duration-300">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300">Security</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/80">
            <p>&copy; 2024 Caterers Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
